// tableFieldUtils.js

import {
    formatPrice,
    resolveFieldCurrencyCode,
} from 'utilities/Currency';
import { formatLocationDisplayValue } from 'utilities/Location';
import { formatMeasurementValue } from 'utilities/Measurement';
import { normalizeNodelistKeys } from 'utilities/NodelistField';
import { normalizeSchemaFieldType, isEnumCompatibleFieldType } from 'utilities/FieldTypes';
import { extractRichTextPlainText } from 'utilities/RichText';
import { formatSignatureFieldDisplay } from 'utilities/SignatureFieldValue';
import {
    DEFAULT_SUBTITLE_FIELD_KEY,
    getNodeSubtitle,
    isSubtitleFieldKey,
    isSubtitleFieldType,
    LEGACY_SUBTITLE_FIELD_KEY,
} from 'utilities/NodeSubtitle';
import { formatTemporalFieldValue, isFirestoreTimestampObject, isTemporalFieldType } from 'views/utils/temporalFieldFormat';

/**
 * tableFieldUtils.js
 * 
 * Utility functions for handling field definitions and values in table views.
 */

/**
 * buildStandardFieldMap
 * 
 * Builds a map of standard fields that are commonly used in table views, outside of any type-specific fields. 
 * These fields are typically present on all nodes and have consistent behavior across types.
 * 
 * @returns {Map} - A map of standard field definitions keyed by field name.
 */
export const buildStandardFieldMap = () => {
    const map = new Map();
    map.set('title', { key: 'title', name: 'title', label: 'Title', type: 'string' });
    map.set('autonumber', { key: 'autonumber', name: 'autonumber', label: 'ID', type: 'number' });
    map.set('autokey', { key: 'autokey', name: 'autokey', label: 'Key', type: 'string' });
    map.set('tags', { key: 'tags', name: 'tags', label: 'Tags', type: 'tags' });
    const subtitleField = {
        key: DEFAULT_SUBTITLE_FIELD_KEY,
        name: DEFAULT_SUBTITLE_FIELD_KEY,
        label: 'Subtitle',
        type: 'subtitle',
    };
    map.set(DEFAULT_SUBTITLE_FIELD_KEY, subtitleField);
    map.set(LEGACY_SUBTITLE_FIELD_KEY, { ...subtitleField, key: LEGACY_SUBTITLE_FIELD_KEY, name: LEGACY_SUBTITLE_FIELD_KEY });
    map.set('dateCreated', { key: 'dateCreated', name: 'dateCreated', label: 'Date Created', type: 'datetime' });
    map.set('dateModified', { key: 'dateModified', name: 'dateModified', label: 'Date Modified', type: 'datetime' });
    return map;
};

/**
 * getFieldType
 * 
 * Determines the field type for a given field definition and field key. This is used to inform how to render 
 * and edit the field in the table view.
 * 
 * @param {object} fieldDef - The field definition object, which may contain a 'type' field.
 * @param {string} fieldKey - The key of the field for which to determine the type.
 * 
 * @return {string|null} - The determined field type as a string, or null if it cannot be determined.
 */
export const getFieldType = (fieldDef, fieldKey) => {
    if (fieldKey === 'title') return 'string';
    if (fieldKey === 'autonumber') return 'number';
    if (fieldKey === 'autokey') return 'string';
    if (fieldKey === 'tags') return 'tags';
    const fieldType = normalizeSchemaFieldType(fieldDef?.type);
    if (isSubtitleFieldType(fieldType)) return 'string';
    return fieldType;
};

/**
 * isInlineEditable
 * 
 * Determines if a field is inline editable based on its definition and key. Typically, string, number, and 
 * boolean fields are considered inline editable.
 * 
 * @param {object} fieldDef - The field definition object, which may contain a 'type' field.
 * @param {string} fieldKey - The key of the field for which to determine if it is inline editable.
 * 
 * @return {boolean} - True if the field is inline editable, false otherwise.
 */
export const isInlineEditable = (fieldDef, fieldKey) => {
    if (fieldKey === 'title') return true;
    if (fieldKey === 'autonumber') return false;
    if (fieldKey === 'autokey') return false;
    if (fieldKey === 'tags') return false;
    const fieldType = String(fieldDef?.type || '').toLowerCase();
    return fieldType === 'string'
        || fieldType === 'sequence'
        || fieldType === 'number'
        || fieldType === 'money'
        || fieldType === 'url'
        || fieldType === 'boolean'
        || isSubtitleFieldType(fieldType);
};

/**
 * getDataSectionName
 * 
 * Constructs the data section name for a given type key. This identifies where
 * type data is stored on a node.
 * 
 * @param {string} typeKey - The key of the node type.
 * 
 * @return {string|null} - The data section name, or null if it cannot be determined.
 */
export const getDataSectionName = (typeKey) => {
    const key = String(typeKey || '').toLowerCase();
    return key ? `data_${key}` : null;
};

/**
 * resolveFieldValue
 * 
 * Resolves the value of a field for a given node and field definition. It handles standard 
 * fields like 'title', 'dateCreated', and 'dateModified', as well as type-specific fields.
 * 
 * @param {object} node - The node object from which to resolve the field value.
 * @param {object} fieldDef - The field definition object, which may contain information about the field.
 * @param {string} dataSection - The data section name where type fields live.
 * 
 * @return {*} - The resolved field value, or undefined if it cannot be resolved.
 */
export const resolveFieldValue = (node, fieldDef, dataSection) => {

    // Validate inputs
    if (!node || !fieldDef) return undefined;

    // Determine the field key from the field definition
    const fieldKey = fieldDef?.name || '';
    const fieldType = String(fieldDef?.type || '').toLowerCase();

    // If the field key is not defined, we cannot resolve a value
    if (!fieldKey) return undefined;

    // Handle standard fields that are not stored in the data section
    if (fieldKey === 'title') return node?.title;
    if (fieldKey === 'autonumber') return node?.autonumber;
    if (fieldKey === 'autokey') return node?.autokey;
    if (isSubtitleFieldKey(fieldKey, fieldType)) return getNodeSubtitle(node);
    if (fieldKey === 'tags' || fieldType === 'tags') return node?.tags;
    if (fieldKey === 'dateCreated') return node?.dateCreated;
    if (fieldKey === 'dateModified') return node?.dateModified;

    // For other fields, we need to look in the appropriate data section based 
    // on the node's type
    const sectionKey = typeof dataSection === 'string' ? dataSection : '';

    // If we don't have a valid section key, we cannot resolve the field value
    if (!sectionKey) return undefined;

    // Attempt to retrieve the field value from the specified data section on the node
    const container = node?.[sectionKey];
    if (container && typeof container === 'object' && container[fieldKey] !== undefined) {
        return container[fieldKey];
    }

    // If we cannot find the field value in the expected location, return undefined
    return undefined;
};

/**
 * formatFieldValue
 * 
 * Formats a field value for display in the table view based on its type. This includes 
 * handling standard fields like 'title', 'dateCreated', and 'dateModified', as well as 
 * type-specific fields.
 * 
 * @param {*} value - The raw field value to format.
 * @param {object} fieldDef - The field definition object, which may contain information about the field type and options.
 * @param {string} fieldKey - The key of the field being formatted.
 * @param {object} [options] - Optional formatting helpers.
 * @param {function} [options.resolveNodelistTitle] - Maps a related node key to a display title.
 * 
 * @return {string} - The formatted field value as a string for display.
 */
export const formatFieldValue = (value, fieldDef, fieldKey, options = {}) => {

    // Handle null or undefined values by returning an empty string
    if (value == null || value === '') return '';

    // For the built-in timestamp fields, we can infer the type as 'datetime' if not 
    // explicitly defined
    const fallbackType = fieldKey === 'dateCreated' || fieldKey === 'dateModified'
        ? 'datetime'
        : (fieldKey === 'tags' ? 'tags' : '');

    // Determine the field type, falling back to 'datetime' for timestamp fields if not 
    // explicitly defined
    const fieldType = String(fieldDef?.type || fallbackType).toLowerCase();

    // Check if the value is a Firestore Timestamp object by looking for 'seconds' 
    // and 'nanoseconds' fields
    const isTimestampObject = isFirestoreTimestampObject(value);

        // LOCATION
        if (fieldType === 'location') {
            return formatLocationDisplayValue(value);
        }

        // BOOLEAN
    // Boolean values should be displayed as 'Yes' or 'No'
    if (fieldType === 'boolean') return value ? 'Yes' : 'No';

        // MONEY
        if (fieldType === 'money') {
            if (value == null || value === '') return '';
            return formatPrice(value, resolveFieldCurrencyCode(fieldDef));
        }

        // CURRENCY CODE
        if (fieldType === 'currency') {
            const code = String(value || '').trim().toUpperCase();
            return code || '';
        }

        // PERCENTAGE
        if (fieldType === 'percentage') {
            const numeric = Number(value);
            if (!Number.isFinite(numeric)) return '';
            const clamped = Math.max(0, Math.min(100, Math.round(numeric)));
            return `${clamped}%`;
        }

        if (fieldType === 'measurement') return formatMeasurementValue(value);

    // DATE
    // Date values should be formatted as localized date strings. We handle both
    // JavaScript Date objects and Firestore Timestamp objects.
    if (isTemporalFieldType(fieldType) || isTimestampObject) {
        return formatTemporalFieldValue({
            value,
            fieldType,
            locale: 'en-US',
            dateOptions: {
                weekday: 'long',
                month: 'long',
                day: 'numeric',
                year: 'numeric',
            },
            dateTimeOptions: {
                weekday: 'long',
                month: 'long',
                day: 'numeric',
                year: 'numeric',
                hour: 'numeric',
                minute: '2-digit',
            },
        });
    }

    // ENUM
    // Enum values should be displayed as their string representation. If the value is null or 
    // undefined, we return an empty string.
    if (isEnumCompatibleFieldType(fieldType)) return value == null ? '' : String(value);

    if (fieldType === 'multiselect') {
        return Array.isArray(value) ? value.join(', ') : String(value);
    }

    if (fieldType === 'richtext') {
        return extractRichTextPlainText(value);
    }

    if (fieldType === 'signature') {
        return formatSignatureFieldDisplay(value);
    }

    if (fieldType === 'url') {
        if (typeof value === 'string' || typeof value === 'number') {
            return String(value);
        }
        if (value && typeof value === 'object') {
            const title = String(value.title || '').trim();
            const url = String(value.url || '').trim();
            if (title && url) return `${title} — ${url}`;
            return title || url || '';
        }
        return '';
    }

    // TAGS
    // Tag values should be displayed as a comma-separated list. If the value is not an array,
    // we convert it to a string. If the field type is 'tags', we assume it should be treated as an 
    // array of strings for display purposes.
    if (fieldType === 'tags') {
        return Array.isArray(value) ? value.join(', ') : String(value);
    }

    if (fieldType === 'checklist') {
        const totalItems = Number(value?.totalItems) || 0;
        const completedItems = Number(value?.completedItems) || 0;
        if (totalItems > 0) return `${completedItems}/${totalItems}`;
        return '';
    }

    if (fieldType === 'relation') {
        const keys = normalizeNodelistKeys(value);
        if (!keys.length) return '';

        const resolveNodelistTitle = options?.resolveNodelistTitle;
        if (typeof resolveNodelistTitle === 'function') {
            return keys
                .map((key) => String(resolveNodelistTitle(key) || '').trim())
                .filter(Boolean)
                .join(', ');
        }

        // Fallback when no title resolver is provided (other views may still hydrate later).
        if (fieldDef?.allowMultiple === false) return keys[0];
        return keys.length === 1 ? keys[0] : `${keys.length} items`;
    }

    // For other field types, we convert the value to a string for display. If the value is an array,
    // we join it with commas. If the value is an object, we stringify it as JSON.
    if (Array.isArray(value)) return value.join(', ');

    // If the value is an object (but not a Date or Timestamp), we convert it to a JSON string for display
    if (typeof value === 'object') return JSON.stringify(value);

    // For all other cases (string, number, etc.), we convert the value to a string for display
    return String(value);
};
